/**
 * AdminABMHCFeaturedMedia — CMS for the "Hope Is Rising in Zimbabwe" hero.
 *
 * Controls: create/edit/delete/publish/unpublish + analytics per row.
 * Backend: /api/abmhc/cms/featured-media/*
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Video, Play, Pause, Trash2, Edit3, BarChart2, Plus, ExternalLink,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import AbmhcEmbedCard from '@/components/abmhc/AbmhcEmbedCard';
import { MediaUpload } from '@/components/MediaUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const PROVIDERS = [
  { value: 'youtube',  label: 'YouTube' },
  { value: 'vimeo',    label: 'Vimeo' },
  { value: 'fwtv',     label: 'Faith Works TV' },
  { value: 'mp4',      label: 'Uploaded MP4' },
  { value: 'hls',      label: 'HLS livestream' },
  { value: 'external', label: 'Approved external embed' },
];
const STATUSES = ['draft', 'awaiting_review', 'approved', 'scheduled',
                    'published', 'unpublished', 'archived'];

const empty = () => ({
  section_title: 'ABMHC | Hope Is Rising in Zimbabwe',
  label: 'ABMHC Documentary',
  video_title: '',
  description: '',
  provider: 'youtube',
  video_url: '',
  fwtv_sermon_id: '',
  fwtv_programme_id: '',
  thumbnail_url: '',
  captions_url: '',
  campaign_id: '',
  donation_url: '/campaigns/abmhc',
  project_url: '/campaigns/abmhc',
  cta_primary_label: 'Support the Vision',
  cta_secondary_label: 'View Project Progress',
  display_position: 'hero',
  is_enabled: true,
  is_featured: false,
  status: 'draft',
  language: 'en',
});

export default function AdminABMHCFeaturedMedia() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/abmhc/cms/featured-media`, { headers: authH() });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(empty()); setShowEditor(true); };
  const openEdit = (r) => { setEditing({ ...r }); setShowEditor(true); };

  const save = async () => {
    const isNew = !editing.id;
    try {
      if (isNew) {
        await axios.post(`${API}/api/abmhc/cms/featured-media`,
          editing, { headers: authH() });
      } else {
        await axios.put(`${API}/api/abmhc/cms/featured-media/${editing.id}`,
          editing, { headers: authH() });
      }
      toast.success('Saved');
      setShowEditor(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const publish = async (r) => {
    try {
      await axios.post(`${API}/api/abmhc/cms/featured-media/${r.id}/publish`, {}, { headers: authH() });
      toast.success('Published'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Publish failed'); }
  };
  const unpublish = async (r) => {
    try {
      await axios.post(`${API}/api/abmhc/cms/featured-media/${r.id}/unpublish`, {}, { headers: authH() });
      toast.success('Unpublished'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Unpublish failed'); }
  };
  const del = async (r) => {
    if (!confirm(`Delete "${r.video_title || r.section_title}"?`)) return;
    try {
      await axios.delete(`${API}/api/abmhc/cms/featured-media/${r.id}`, { headers: authH() });
      toast.success('Deleted'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  const loadAnalytics = async (r) => {
    try {
      const res = await axios.get(`${API}/api/abmhc/cms/featured-media/${r.id}/analytics`, { headers: authH() });
      setAnalytics({ media: r, data: res.data });
    } catch (e) { toast.error('Analytics unavailable'); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6" data-testid="admin-abmhc-fm-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <header className="flex items-center gap-3">
          <Video className="h-7 w-7 text-amber-300" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">ABMHC Featured Media</h1>
            <p className="text-sm text-slate-400">Manage the "Hope Is Rising in Zimbabwe" hero video on abmhc.org.</p>
          </div>
          <Button onClick={openNew} data-testid="abmhc-fm-new"><Plus className="h-4 w-4 mr-1" />New</Button>
        </header>

        <AbmhcEmbedCard />

        {rows.length === 0 && !busy && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-6 text-center text-slate-400">
              No featured media configured yet. Click <strong>New</strong> to add the first one.
            </CardContent>
          </Card>
        )}

        {rows.map(r => (
          <Card key={r.id} className="bg-slate-900/60 border-slate-800" data-testid={`abmhc-fm-row-${r.id}`}>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-sm truncate">{r.video_title || r.section_title}</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">{r.provider} · {r.language} · {r.display_position}</p>
              </div>
              <Badge variant="outline" className={
                r.status === 'published' ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10'
                : r.status === 'draft' ? 'border-slate-600 text-slate-400'
                : 'border-amber-400 text-amber-300 bg-amber-500/10'
              } data-testid={`abmhc-fm-status-${r.id}`}>{r.status}</Badge>
              {r.embed?.error && (
                <Badge variant="outline" className="border-rose-400 text-rose-300 bg-rose-500/10 text-[10px]"
                          data-testid={`abmhc-fm-embed-error-${r.id}`}>
                  {r.embed.error}
                </Badge>
              )}
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(r)}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        data-testid={`abmhc-fm-edit-${r.id}`}>
                <Edit3 className="h-3 w-3 mr-1" />Edit
              </Button>
              {r.status !== 'published' ? (
                <Button size="sm" onClick={() => publish(r)}
                          disabled={!!r.embed?.error}
                          data-testid={`abmhc-fm-publish-${r.id}`}>
                  <Play className="h-3 w-3 mr-1" />Publish
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => unpublish(r)}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"
                          data-testid={`abmhc-fm-unpublish-${r.id}`}>
                  <Pause className="h-3 w-3 mr-1" />Unpublish
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => loadAnalytics(r)}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        data-testid={`abmhc-fm-stats-${r.id}`}>
                <BarChart2 className="h-3 w-3 mr-1" />Analytics
              </Button>
              <Button size="sm" variant="outline" onClick={() => del(r)}
                        className="border-rose-700 text-rose-300 hover:bg-rose-500/20"
                        data-testid={`abmhc-fm-delete-${r.id}`}>
                <Trash2 className="h-3 w-3 mr-1" />Delete
              </Button>
              {r.embed?.embed_url && (
                <a href={r.embed.embed_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1 ml-auto"
                    data-testid={`abmhc-fm-preview-${r.id}`}>
                  Preview <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Editor dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'New'} featured media</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Row label="Section title">
                <Input value={editing.section_title} onChange={e => setEditing({ ...editing, section_title: e.target.value })}
                          className="bg-slate-900 border-slate-800" data-testid="abmhc-field-section_title" />
              </Row>
              <Row label="Video title">
                <Input value={editing.video_title} onChange={e => setEditing({ ...editing, video_title: e.target.value })}
                          className="bg-slate-900 border-slate-800" data-testid="abmhc-field-video_title" />
              </Row>
              <Row label="Description">
                <Textarea rows={3} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })}
                             className="bg-slate-900 border-slate-800" data-testid="abmhc-field-description" />
              </Row>
              <Row label="Provider">
                <Select value={editing.provider} onValueChange={v => setEditing({ ...editing, provider: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="abmhc-field-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Row>
              {editing.provider !== 'fwtv' && (
                <Row label="Video URL">
                  <Input value={editing.video_url} onChange={e => setEditing({ ...editing, video_url: e.target.value })}
                            className="bg-slate-900 border-slate-800" data-testid="abmhc-field-video_url"
                            placeholder="https://youtube.com/watch?v=… or the ID directly" />
                </Row>
              )}
              {editing.provider === 'fwtv' && (
                <>
                  <Row label="FWTV Sermon ID">
                    <Input value={editing.fwtv_sermon_id} onChange={e => setEditing({ ...editing, fwtv_sermon_id: e.target.value })}
                              className="bg-slate-900 border-slate-800" data-testid="abmhc-field-fwtv_sermon_id" />
                  </Row>
                  <Row label="OR FWTV Programme ID">
                    <Input value={editing.fwtv_programme_id} onChange={e => setEditing({ ...editing, fwtv_programme_id: e.target.value })}
                              className="bg-slate-900 border-slate-800" data-testid="abmhc-field-fwtv_programme_id" />
                  </Row>
                </>
              )}
              <Row label="Thumbnail">
                <MediaUpload value={editing.thumbnail_url} onChange={(url) => setEditing({ ...editing, thumbnail_url: url })}
                          purpose="cover" testId="abmhc-field-thumbnail_url" previewClassName="h-16" />
              </Row>
              <Row label="Captions (VTT/SRT)">
                <MediaUpload value={editing.captions_url} onChange={(url) => setEditing({ ...editing, captions_url: url })}
                          purpose="general" testId="abmhc-field-captions_url" />
              </Row>
              <div className="grid grid-cols-2 gap-3">
                <Row label="Donation URL">
                  <Input value={editing.donation_url} onChange={e => setEditing({ ...editing, donation_url: e.target.value })}
                            className="bg-slate-900 border-slate-800" data-testid="abmhc-field-donation_url" />
                </Row>
                <Row label="Project URL">
                  <Input value={editing.project_url} onChange={e => setEditing({ ...editing, project_url: e.target.value })}
                            className="bg-slate-900 border-slate-800" data-testid="abmhc-field-project_url" />
                </Row>
                <Row label="Primary CTA label">
                  <Input value={editing.cta_primary_label} onChange={e => setEditing({ ...editing, cta_primary_label: e.target.value })}
                            className="bg-slate-900 border-slate-800" data-testid="abmhc-field-cta_primary_label" />
                </Row>
                <Row label="Secondary CTA label">
                  <Input value={editing.cta_secondary_label} onChange={e => setEditing({ ...editing, cta_secondary_label: e.target.value })}
                            className="bg-slate-900 border-slate-800" data-testid="abmhc-field-cta_secondary_label" />
                </Row>
                <Row label="Status">
                  <Select value={editing.status} onValueChange={v => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="abmhc-field-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Language">
                  <Input value={editing.language} onChange={e => setEditing({ ...editing, language: e.target.value })}
                            className="bg-slate-900 border-slate-800" data-testid="abmhc-field-language" />
                </Row>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!editing.is_enabled} onCheckedChange={v => setEditing({ ...editing, is_enabled: v })}
                             data-testid="abmhc-field-is_enabled" />
                  Enabled
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!editing.is_featured} onCheckedChange={v => setEditing({ ...editing, is_featured: v })}
                             data-testid="abmhc-field-is_featured" />
                  Featured (surfaced first)
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
            <Button onClick={save} data-testid="abmhc-fm-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics dialog */}
      <Dialog open={!!analytics} onOpenChange={() => setAnalytics(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-md">
          <DialogHeader><DialogTitle>Analytics — {analytics?.media?.video_title}</DialogTitle></DialogHeader>
          {analytics && (
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Starts" value={analytics.data.starts} />
              <Stat label="Completions" value={analytics.data.completions} />
              <Stat label="Donate clicks" value={analytics.data.donate_clicks} />
              <Stat label="Project clicks" value={analytics.data.project_clicks} />
              <Stat label="Shares" value={analytics.data.shares} />
              <Stat label="Total events" value={analytics.data.total_events} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-400">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="border border-slate-800 rounded-md p-3 bg-slate-800/30">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl font-semibold text-white mt-1">{value}</div>
  </div>
);
